import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { types } from 'mobx-state-tree';
import { api } from 'src/api';
import { createAsync } from 'src/utils/asyncModel';

/** Guids come back from different services and may differ in case, so both maps are keyed in one. */
const keyOf = id => String(id).toLowerCase();

/**
 * The company's AppFolio chart of accounts. Read whole because it is what turns an account id into
 * something a person can read, and the ids on a bill's properties could be any of them.
 */
const listGlAccountsAsync = createAsync(() => {
  return async function listGlAccounts(options) {
    const resp = await api.Credentials.getGLAccounts(options);
    if (Array.isArray(resp === null || resp === void 0 ? void 0 : resp.response)) this.setGlAccounts(resp.response);
    return resp;
  };
});
const listPropertiesAsync = createAsync(() => {
  return async function listProperties(options) {
    const resp = await api.AppfolioProperties.listProperties(options);
    if (resp !== null && resp !== void 0 && resp.isSuccessful && Array.isArray(resp.response)) this.setProperties(resp.response);
    return resp;
  };
});

/**
 * What one push attempt posted to. One attempt at a time is right for this action: the details view
 * shows one bill, so a read for a newly opened bill should cancel the one still out for the last.
 */
const readPushGlAccountsAsync = createAsync(pushAttemptId => {
  return async function readPushGlAccounts(options) {
    const resp = await api.AppfolioBills.getPushGlAccounts({
      pushAttemptId
    }, options);

    // A 404 is a real answer here — the attempt is not this company's, or predates the stored body —
    // but it is not one this map can hold, so it stays unread and the caller keeps saying so.
    if (resp !== null && resp !== void 0 && resp.isSuccessful && resp.response) this.setRecordedAccounts(pushAttemptId, resp.response);
    return resp;
  };
});

/**
 * Which GL account each property's payables post to, and what that account is called.
 *
 * Two reads that are only useful together: the property says which account, the chart of accounts says
 * what it is. Kept out of the wider credentials store so the pages that read these for their own
 * purposes cannot cancel a read this one is waiting on.
 */
const AppfolioGlAccountsStore = types.model('AppfolioGlAccountsStore', {
  glAccountsByExternalId: types.optional(types.frozen(), {}),
  propertiesByLivableId: types.optional(types.frozen(), {}),
  /** Whether each read has answered, so "not configured" can be told from "not read yet". */
  hasReadGlAccounts: types.optional(types.boolean, false),
  hasReadProperties: types.optional(types.boolean, false),
  /** What each push attempt posted to, once read. Keyed by attempt, because that is what it describes. */
  recordedByAttemptId: types.optional(types.frozen(), {}),
  listGlAccountsAsync,
  listPropertiesAsync,
  readPushGlAccountsAsync
}).actions(store => ({
  setRecordedAccounts(pushAttemptId, recorded) {
    store.recordedByAttemptId = _objectSpread(_objectSpread({}, store.recordedByAttemptId), {}, {
      [keyOf(pushAttemptId)]: recorded
    });
  },
  setGlAccounts(accounts) {
    store.glAccountsByExternalId = Object.fromEntries(accounts.filter(account => account === null || account === void 0 ? void 0 : account.externalId).map(account => [keyOf(account.externalId), account]));
    store.hasReadGlAccounts = true;
  },
  setProperties(properties) {
    store.propertiesByLivableId = Object.fromEntries(properties.filter(property => property === null || property === void 0 ? void 0 : property.propertyLivableId).map(property => [keyOf(property.propertyLivableId), property]));
    store.hasReadProperties = true;
  }
})).views(store => ({
  /**
   * The account a payable for this property posts to, or null when the property has none configured,
   * is not mapped to AppFolio, or has not been read yet — `hasRead` is what separates those.
   */
  getApGlAccount(propertyLivableId) {
    var _store$propertiesByLi, _store$glAccountsByEx;
    if (!propertyLivableId) return null;
    const externalId = (_store$propertiesByLi = store.propertiesByLivableId[keyOf(propertyLivableId)]) === null || _store$propertiesByLi === void 0 ? void 0 : _store$propertiesByLi.accountsPayableGlAccountExternalId;
    return externalId ? (_store$glAccountsByEx = store.glAccountsByExternalId[keyOf(externalId)]) !== null && _store$glAccountsByEx !== void 0 ? _store$glAccountsByEx : null : null;
  },
  /** An account by its AppFolio id, which is the form a recorded line names it in. */
  getGlAccountByExternalId(externalId) {
    var _store$glAccountsByEx2;
    return externalId ? (_store$glAccountsByEx2 = store.glAccountsByExternalId[keyOf(externalId)]) !== null && _store$glAccountsByEx2 !== void 0 ? _store$glAccountsByEx2 : null : null;
  },
  /** What one attempt posted to, or null until its read has landed. */
  getRecordedAccounts(pushAttemptId) {
    var _store$recordedByAtte;
    return pushAttemptId ? (_store$recordedByAtte = store.recordedByAttemptId[keyOf(pushAttemptId)]) !== null && _store$recordedByAtte !== void 0 ? _store$recordedByAtte : null : null;
  },
  get hasRead() {
    return store.hasReadGlAccounts && store.hasReadProperties;
  }
}));
export const appfolioGlAccounts = types.optional(AppfolioGlAccountsStore, {});